import { useQuery } from "@tanstack/react-query";
import { PersonSearchResponse, PersonWorldResponse, WorldSettings, WorldsResponse, type WorldVisibility } from "@townsy/contracts";
import { API_BASE, apiFetch, isApiError } from "./client";

/**
 * Named world copies (ADR-0043): the open copies, which world a person is in (the server applies their privacy setting),
 * "Find a person", and the account's own "Who can see which world I'm in". Nothing here decides anything: every answer
 * comes from the server, which may simply say "not shown".
 */
export const worldEndpoints = {
  worlds: `${API_BASE}/worlds`,
  personWorld: (actorId: string) => `${API_BASE}/people/${encodeURIComponent(actorId)}/world`,
  search: (q: string) => `${API_BASE}/people/search?q=${encodeURIComponent(q)}`,
  settings: `${API_BASE}/me/world-settings`,
};

export const worldKeys = {
  worlds: ["worlds"] as const,
  personWorld: (actorId: string) => ["worlds", "person", actorId] as const,
  search: (q: string) => ["worlds", "search", q] as const,
  settings: ["worlds", "settings"] as const,
};

export const fetchWorlds = (): Promise<WorldsResponse> => apiFetch(worldEndpoints.worlds, { schema: WorldsResponse });
export const fetchPersonWorld = (actorId: string): Promise<PersonWorldResponse> => apiFetch(worldEndpoints.personWorld(actorId), { schema: PersonWorldResponse });
export const searchPeople = (q: string): Promise<PersonSearchResponse> => apiFetch(worldEndpoints.search(q), { schema: PersonSearchResponse });
export const fetchWorldSettings = (): Promise<WorldSettings> => apiFetch(worldEndpoints.settings, { schema: WorldSettings });
export const putWorldSettings = (visibility: WorldVisibility): Promise<WorldSettings> =>
  apiFetch(worldEndpoints.settings, { method: "PUT", body: { visibility }, schema: WorldSettings });

const noAuthRetry = (count: number, err: unknown) => !(isApiError(err) && [401, 403, 404, 422, 429].includes(err.status)) && count < 2;

/** The open copies, refreshed while the Worlds dialog is open. */
export function useWorldsQuery(enabled: boolean) {
  return useQuery({ queryKey: worldKeys.worlds, queryFn: fetchWorlds, enabled, staleTime: 0, refetchInterval: enabled ? 10_000 : false, retry: noAuthRetry });
}

/** Which world a person is in, when the server shows it (null otherwise). Re-read every 20 s while it is on screen. */
export function usePersonWorldQuery(actorId: string | null) {
  return useQuery({
    queryKey: worldKeys.personWorld(actorId ?? "none"),
    queryFn: () => fetchPersonWorld(actorId!),
    enabled: Boolean(actorId),
    staleTime: 10_000,
    refetchInterval: actorId ? 20_000 : false,
    retry: noAuthRetry,
  });
}

/** "Find a person" results for a query the user submitted (at least 3 characters; the server validates again). */
export function usePersonSearchQuery(q: string | null) {
  return useQuery({ queryKey: worldKeys.search(q ?? ""), queryFn: () => searchPeople(q!), enabled: Boolean(q), staleTime: 5_000, retry: noAuthRetry });
}

export function useWorldSettingsQuery(enabled: boolean) {
  return useQuery({ queryKey: worldKeys.settings, queryFn: fetchWorldSettings, enabled, retry: noAuthRetry });
}
